import JSON_Pointer from 'json-pointer';
import { getErrorPropName, isCheckbox } from '../../ValidationForm/validationFormUtils';
import get from '../../../utils/get';
function isText(uiSchema, propName) {
  var uiProp = uiSchema[propName];
  return uiProp && (uiProp['ui:widget'] === 'text' || uiProp['ui:widget'] === 'textarea' || uiProp['ui:widget'] === 'richtext');
}
function isRichText(uiSchema, propName) {
  var uiProp = uiSchema[propName];
  return uiProp && uiProp['ui:widget'] === 'richtext';
}
function toPathArray(path) {
  return path.split('.').filter(function (val) {
    return val.length > 0;
  }).map(function (val) {
    var indexStart = val.indexOf('[');
    return indexStart !== -1 ? val.substring(0, val.indexOf('[')) : val;
  });
}
function toSchemaPropertyJsonPointer(path, schema) {
  var pathParts = toPathArray(path);
  var result = "/properties/".concat(pathParts[0]);
  if (pathParts.length > 1) {
    var typePath = "".concat(result, "/type");
    var type = JSON_Pointer.get(schema, typePath);
    result = type === 'array' ? "".concat(result, "/items/properties/").concat(pathParts[1]) : "".concat(result, "/properties/").concat(pathParts[1]);
  }
  return result;
}
function getPropertyDefinitions(schema, jsonPointerPath) {
  var propertySchema = JSON_Pointer.get(schema, jsonPointerPath);
  var propertyDefinitions = propertySchema.$ref ? JSON_Pointer.get(schema, propertySchema.$ref.substr(1)) : propertySchema;
  return propertyDefinitions;
}
function getEnumName(schema, jsonPointerPath, enumValue) {
  var result;
  var propertyDefinitions = getPropertyDefinitions(schema, jsonPointerPath);
  if (propertyDefinitions.enum && propertyDefinitions.enum.length && propertyDefinitions.enumNames && propertyDefinitions.enumNames.length) {
    var valueIndex = propertyDefinitions.enum.indexOf(enumValue);
    result = propertyDefinitions.enumNames[valueIndex];
  }
  return result;
}
var REQUIRED_PATH_PART = '/required/';
var CONST_PATH_SUFFIX = '/const';
var getAllowedLength = get('params.allowedValues.length');
var getDefinitionLength = get('enum.length');
export default function getTransformErrors(intl, schema, uiSchema) {
  return function (errors) {
    var anyOfErrors = errors.filter(function (error) {
      return error.name === 'anyOf';
    });
    var filtered = errors.filter(function (error) {
      var propName = getErrorPropName(error);
      if (error.name === 'maxLength' && uiSchema && isRichText(uiSchema, propName)) {
        return false;
      }

      // PRO-10527: can happen with dependent fields; an error will appear on
      //	the trigger field and the dependent field. Only want to show the
      //	dependent field error.
      if (error.name === 'enum' && error.message === 'should be equal to one of the allowed values') {
        var propertySchemaPath = toSchemaPropertyJsonPointer(error.property, schema);
        var propertyDefinitions = getPropertyDefinitions(schema, propertySchemaPath);
        var allowedLength = getAllowedLength(error);
        var definitionLength = getDefinitionLength(propertyDefinitions);
        if (allowedLength !== definitionLength) {
          return false;
        }
      }
      return true;
    });
    return filtered.map(function (error) {
      var propName = getErrorPropName(error);
      switch (error.name) {
        case 'const':
          var allowedValue = error.params.allowedValue;
          var propertySchemaPath = toSchemaPropertyJsonPointer(error.property, schema);
          var allowedDisplay = getEnumName(schema, propertySchemaPath, allowedValue);
          if (allowedDisplay) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.constant'
            }, {
              value: allowedDisplay
            });
          } else if (allowedValue === true) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.required'
            });
          }
          break;
        case 'required':
          var anyOfError = anyOfErrors.find(function (_err) {
            return error.schemaPath.indexOf(_err.schemaPath) === 0;
          });
          var anyOfErrorSchema = null;
          if (anyOfError) {
            try {
              anyOfErrorSchema = JSON_Pointer.get(schema, anyOfError.schemaPath.substr(1));
            } catch (err) {
              anyOfErrorSchema = null;
            }
          }
          if (anyOfErrorSchema) {
            var requiredSchemaPath = error.schemaPath.substr(1);
            var constValue = '';
            var isAnyConstError = anyOfErrorSchema.find(function (anyOfEntry) {
              var anyConstErrorDict = JSON_Pointer.dict(anyOfEntry);
              var pathKeys = Object.keys(anyConstErrorDict);
              var isMatchingPath = pathKeys.find(function (path) {
                var requiredPath = path.substring(0, path.lastIndexOf(REQUIRED_PATH_PART) + REQUIRED_PATH_PART.length - 1);
                return requiredSchemaPath.endsWith(requiredPath);
              }) !== undefined;
              var constSuffix = "".concat(error.params.missingProperty).concat(CONST_PATH_SUFFIX);
              var isMatchingConst = pathKeys.find(function (path) {
                var result = path.endsWith(constSuffix);
                if (result) {
                  constValue = anyConstErrorDict[path];
                }
                return path.endsWith(constSuffix);
              }) !== undefined;
              return isMatchingConst && isMatchingPath;
            }) !== undefined;
            if (isAnyConstError) {
              var _propertySchemaPath = toSchemaPropertyJsonPointer(error.property, schema);
              var _allowedDisplay = getEnumName(schema, _propertySchemaPath, constValue);
              if (_allowedDisplay) {
                error.message = intl.formatMessage({
                  id: '@dm/components.recordEditorForm.error.constant'
                }, {
                  value: _allowedDisplay
                });
              }
            } else {
              error.message = intl.formatMessage({
                id: '@dm/components.recordEditorForm.error.date.required'
              }, {
                limit: error.params.limit
              });
            }
          } else {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.required'
            });
          }
          break;
        case 'dependencies':
          var params = error.params; // Scenario -- multi-part date field inside array/DSA where
          //	supplying smaller date part causes larger to become required.
          if (params && params.depsCount === 1 && params.deps === params.missingProperty) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.required'
            });
          }
          break;
        case 'maxItems':
          if (uiSchema && isCheckbox(uiSchema, propName)) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.checkboxes.maxItems'
            }, {
              limit: error.params.limit
            });
          }
          break;
        case 'minItems':
          if (uiSchema && isCheckbox(uiSchema, propName)) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.checkboxes.minItems'
            }, {
              limit: error.params.limit
            });
          }
          break;
        case 'maxLength':
          if (uiSchema && isText(uiSchema, propName)) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.text.maxLength'
            }, {
              limit: error.params.limit
            });
          }
          break;
        case 'minLength':
          if (uiSchema && isText(uiSchema, propName)) {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.text.minLength'
            }, {
              limit: error.params.limit
            });
          }
          break;
        case 'type':
          if (error.params.type === 'integer') {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.numeric.notInteger'
            });
          } else if (error.params.type === 'number') {
            error.message = intl.formatMessage({
              id: '@dm/components.recordEditorForm.error.numeric.notNumber'
            });
          }
          break;
        case 'maximum':
          error.message = intl.formatMessage({
            id: '@dm/components.recordEditorForm.error.numeric.aboveMaximum'
          }, {
            limit: error.params.limit
          });
          break;
        case 'minimum':
          error.message = intl.formatMessage({
            id: '@dm/components.recordEditorForm.error.numeric.belowMinimum'
          }, {
            limit: error.params.limit
          });
          break;
      }
      return error;
    });
  };
}